import React from 'react';
import 'react-datepicker/dist/react-datepicker.css';

import { Promotion, PromotionStatus } from './types';
import { Badge } from '../component/Badge';
import { ChangePromotionStatus } from '../component/ChangePromotionStatus';
import { PromotionPageShell } from '../component/Promotions/PromotionPageShell';
import { TextInput } from '../component/TextInput';
import { formatDateTime } from '../utils';

export const ViewFunnelPromotion = ({
  promotionData,
  onRefresh,
}: {
  promotionData: Promotion;
  onRefresh: () => void;
}) => {
  const autoRenewEnabled = !!promotionData?.auto_renew;

  return (
    <PromotionPageShell
      title="View Funnel Promotion"
      description="This promotion will apply to trial orders only. When this promotion is active, the free product(s) will be shown to customers on the Funnel checkout page, and a banner will alert them to the presence of the active promotion."
    >
      <div className="bg-white mt-4 mb-4 rounded-lg shadow-md p-4 min-w-[550px] relative border">
        <div className="text-md text-left">
          <div className="w-full mb-2">
            <h2>Promotion name</h2>
            <TextInput placeholder="None" value={promotionData?.name} disabled />
          </div>
        </div>

        <div className="w-full mb-2">
          <div className="flex flex-row justify-between">
            <div className="flex-1 mr-4">
              <h2>Status</h2>
              <div className="flex">
                {promotionData && (
                  <ChangePromotionStatus
                    isActive={promotionData?.status === PromotionStatus.ACTIVE}
                    id={promotionData.id}
                    name={promotionData.name}
                    reFetch={onRefresh}
                  />
                )}
              </div>
            </div>
            <div className="flex-1">
              <h2>Creator</h2>
              <Badge label={promotionData?.created_by ?? 'n/a'} />
            </div>
          </div>
        </div>
        <div className="flex">
          <div className="mb-2 mr-2 flex-1">
            <h2 className="mb-2">Currency</h2>
            <TextInput placeholder="None" value={promotionData?.currency} disabled />
          </div>
          <div className="mb-2 mr-2 flex-1">
            <h2 className="mb-2">Eligible country</h2>
            <TextInput placeholder="None" value={promotionData?.eligible_country} disabled />
          </div>
        </div>
      </div>

      <div className="bg-white mb-4 rounded-lg shadow-md p-4 min-w-[550px] relative border">
        <h2>
          Funnel Promotion Banner <span className="text-xs">(Optional)</span>
        </h2>
        <p className="text-xs text-gray-500 font-normal mb-2">
          Customer-facing banner to be displayed in the funnel
        </p>
        <div className="w-full mb-2">
          <h3 className="flex">All Pages (desktop & mobile)</h3>
          <TextInput placeholder="None" value={promotionData?.banner_copy} disabled />
        </div>
      </div>

      <div className="bg-white mb-4 rounded-lg shadow-md p-4 min-w-[550px] relative border">
        <h2>
          Marketing Site Promotion Banner <span className="text-xs">(Optional)</span>
        </h2>
        <p className="text-xs text-gray-500 font-normal mb-2">
          Customer-facing banner to be displayed on the marketing site
        </p>
        <div className="w-full mb-2">
          <h3 className="flex">Desktop Copy</h3>
          <TextInput
            placeholder="None"
            value={promotionData?.marketing_site_desktop_banner_copy}
            disabled
          />
        </div>

        <div className="w-full mb-2">
          <h3 className="flex">Mobile Copy</h3>
          <TextInput
            placeholder="None"
            value={promotionData?.marketing_site_mobile_banner_copy}
            disabled
          />
        </div>
      </div>

      <div className="bg-white mb-4 rounded-lg shadow-md p-4 min-w-[550px] relative border">
        <h2>
          Active dates <span className="text-xs">(Optional)</span>
        </h2>
        <p className="text-xs text-gray-500 font-normal">
          The customer will only receive this promotion if they check out with their trial order
          within this timeframe
        </p>

        <div className="flex mt-2">
          <div className="w-1/2 pr-2">
            <h3>Start Date</h3>
            <div className="w-full mb-2">
              <span>
                {promotionData?.start_timestamp
                  ? formatDateTime(promotionData.start_timestamp, true)
                  : 'Not set'}
              </span>
            </div>
          </div>
          <div className="w-1/2">
            <h3>End Date</h3>
            <div className="w-full mb-2">
              <span>
                {promotionData?.end_timestamp
                  ? formatDateTime(promotionData.end_timestamp, true)
                  : 'Not set'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white mb-4 rounded-lg shadow-md p-4 min-w-[550px] relative border">
        <div className="w-full mb-2">
          <h2>Free promotional product(s)</h2>
          <div className="flex justify-between space-x-4">
            <div className="w-3/4">
              <TextInput placeholder="None" value={promotionData?.free_product_code} disabled />
            </div>
            <div className="w-1/4">
              <TextInput
                placeholder="None"
                value={promotionData?.quantity ? promotionData.quantity.toString() : ''}
                disabled
              />
            </div>
          </div>
        </div>
        <div className="w-full mb-2">
          <h2>Auto-renew into a paid subscription product?</h2>
          <div className="flex flex-col space-y-2 mt-2">
            <label className="flex items-center space-x-2 cursor-not-allowed">
              <input
                type="radio"
                name="autoRenewPaidSubscriptionView"
                value="no"
                className="accent-black"
                checked={!autoRenewEnabled}
                readOnly
              />
              <span>No</span>
            </label>
            <p className="text-xs text-gray-500 font-normal ml-6">
              Free product will be added to trial order, but it will not renew as a paid
              subscription product
            </p>
            <label className="flex items-center space-x-2 cursor-not-allowed">
              <input
                type="radio"
                name="autoRenewPaidSubscriptionView"
                value="yes"
                className="accent-black"
                checked={autoRenewEnabled}
                readOnly
              />
              <span>Yes</span>
            </label>
            <p className="text-xs text-gray-500 font-normal ml-6">
              Free product will be added to trial order and it will renew as a paid subscription
              product on the customer’s next subscription order
            </p>
          </div>
        </div>

        {autoRenewEnabled && (
          <>
            <div className="w-full my-4">
              <h2>Paid subscription product</h2>
              <div className="flex justify-between space-x-4">
                <div className="w-3/4">
                  <TextInput
                    placeholder="None"
                    value={promotionData?.paid_product_code ?? ''}
                    disabled
                  />
                </div>
                <div className="w-1/4">
                  <TextInput
                    placeholder="None"
                    value={
                      promotionData?.paid_quantity !== null &&
                      promotionData?.paid_quantity !== undefined
                        ? promotionData.paid_quantity.toString()
                        : ''
                    }
                    disabled
                  />
                </div>
              </div>
            </div>
            <div className="w-full my-4">
              <h2>
                Disclaimer <span className="text-xs">(Optional)</span>
              </h2>
              <p className="text-xs text-gray-500 font-normal mb-2">
                Customer-facing disclaimer to be displayed on funnel checkout page
              </p>
              <div className="w-full mb-2">
                <TextInput placeholder="None" value={promotionData?.disclaimer ?? ''} disabled />
              </div>
            </div>
          </>
        )}
      </div>

      <div className="bg-white mb-4 rounded-lg shadow-md p-4 min-w-[550px] relative border">
        <div className="w-full mb-2">
          <h2>Conditional product(s) for promotion eligibility</h2>
          <div className="flex justify-between space-x-4">
            <div className="w-full">
              <TextInput
                placeholder="None"
                value={
                  promotionData?.required_conditions_products
                    ? promotionData.required_conditions_products.join(', ')
                    : 'None'
                }
                disabled
              />
            </div>
          </div>
        </div>
        <div className="w-full mb-2">
          <h2>Conditional spend amount for promotion eligibility</h2>
          <div className="flex justify-between space-x-4">
            <div className="w-full">
              <TextInput
                placeholder="None"
                value={
                  promotionData?.minimum_order_gross_value_threshold_amount
                    ? `$ ${promotionData.minimum_order_gross_value_threshold_amount.toString()}`
                    : '$0.00'
                }
                disabled
              />
            </div>
          </div>
        </div>
      </div>
    </PromotionPageShell>
  );
};
